/**
 * Web A/V permission handling.
 * Uses the browser MediaDevices API.
 */

import { useCallback } from 'react'

export type PermissionStatus = {
  camera: boolean
  microphone: boolean
}

async function queryPermission(name: 'camera' | 'microphone'): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({ name: name as PermissionName })
    return status.state === 'granted'
  } catch {
    return false
  }
}

function openSettings() {
  // Browsers don't support programmatic settings navigation.
  // The permission prompt itself is the "settings".
  console.warn('Cannot open browser settings programmatically. User must change permissions in browser settings.')
}

export function useAVPermissions() {
  const checkPermissions = useCallback(async (): Promise<PermissionStatus> => {
    const [camera, microphone] = await Promise.all([queryPermission('camera'), queryPermission('microphone')])
    return { camera, microphone }
  }, [])

  const requestPermissions = useCallback(
    async (needs: { camera?: boolean; microphone?: boolean }): Promise<PermissionStatus> => {
      const constraints: MediaStreamConstraints = {}
      if (needs.camera) constraints.video = true
      if (needs.microphone) constraints.audio = true

      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        for (const track of stream.getTracks()) {
          track.stop()
        }
        const result = {
          camera: needs.camera ?? false,
          microphone: needs.microphone ?? false
        }
        return result
      } catch (err) {
        console.error('[AV:permissions] denied or error', err)
        return { camera: false, microphone: false }
      }
    },
    []
  )

  return { checkPermissions, requestPermissions, openSettings }
}
